import React, { useState } from 'react';
import { MoreVertical } from 'lucide-react';

import Button from '@dashboard/components/Button';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@dashboard/components/ui/accordion';
import { Badge } from '@dashboard/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { cn } from '@dashboard/lib/utils';

import { getPriceHeadline, getPriceTypeName } from '../PricesList/priceHelpers';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';

import bumpUsageLabel from './bumpUsageLabel';
import describeBumpRule from './describeBumpRule';
import BumpProductForm from './BumpProductForm';
import OrderBumpInfoDialog from './OrderBumpInfoDialog';
import { ORDER_BUMP_COPY } from './offerCopy';

function ActionsMenu({
  onInfo,
  isEnabled,
  onToggleEnabled,
  onDuplicate,
  onRemove,
  onDelete,
  canDelete = true,
  copy = ORDER_BUMP_COPY,
}) {
  return (
    // Non-modal: Remove and Delete open a confirm dialog as the menu closes, and
    // a modal menu then leaves pointer-events: none on the body.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          appearance="icon-button"
          size="narrow"
          type="button"
          aria-label={`${copy.Noun} actions`}
          className="size-7"
        >
          <MoreVertical className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onInfo}>Info</DropdownMenuItem>
        {onDuplicate && <DropdownMenuItem onSelect={onDuplicate}>Duplicate</DropdownMenuItem>}
        {onToggleEnabled && (
          <DropdownMenuItem onSelect={onToggleEnabled}>
            {isEnabled ? 'Disable on this page' : 'Enable on this page'}
          </DropdownMenuItem>
        )}
        {/* Remove unplaces the bump from this page; the product survives for
            other pages and the picker. Delete takes the product with it, which
            is why it sits alone below the rule. */}
        <DropdownMenuItem onSelect={onRemove}>Remove from this page</DropdownMenuItem>

        {onDelete && (
          <>
            <DropdownMenuSeparator />
            {/* The reason rides inside the item rather than a tooltip: a tooltip
                trigger is a <button>, which cannot wrap a menu item, and a
                disabled item takes no pointer events to open one with. */}
            <DropdownMenuItem variant="destructive" disabled={!canDelete} onSelect={onDelete}>
              <span className="flex flex-col">
                Delete from all pages
                {!canDelete && (
                  <span className="text-xs text-muted-foreground">
                    Purchased {copy.plural} cannot be deleted
                  </span>
                )}
              </span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// A slot whether or not there is an image: an order bump card almost always has
// one, so a row without the space reads as a different kind of row. Same shape as
// CheckoutListItem's thumbnail, sized down for a list row.
function Thumbnail({ media }) {
  const image = media?.[0];

  if (image?.url) {
    const { src, srcSet } = getImageSrcAndSrcSet({ url: image.url, width: 64, height: 48 });

    return (
      <img
        alt=""
        src={src}
        srcSet={srcSet}
        className="h-9 w-12 shrink-0 rounded bg-gray-50 dark:bg-card object-cover"
      />
    );
  }

  return (
    <div className="flex h-9 w-12 shrink-0 items-center justify-center rounded border border-gray-100 dark:border-border bg-gray-50 dark:bg-card">
      <PhotoIcon className="w-5 text-gray-200" />
    </div>
  );
}

function DragHandle({ dragHandleProps }) {
  if (!dragHandleProps) return null;

  return (
    <div
      {...dragHandleProps}
      className="cursor-grab active:cursor-grabbing text-gray-400 dark:text-muted-foreground shrink-0 p-1"
      aria-label="Drag to reorder"
    >
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path d="M7 2a1 1 0 000 2 1 1 0 000-2zm4 0a1 1 0 000 2 1 1 0 000-2zM7 8a1 1 0 000 2 1 1 0 000-2zm4 0a1 1 0 000 2 1 1 0 000-2zm-4 6a1 1 0 000 2 1 1 0 000-2zm4 0a1 1 0 000 2 1 1 0 000-2z" />
      </svg>
    </div>
  );
}

export default function OrderBumpItem({
  pageId,
  entry,
  isOpen,
  onOpenChange,
  // The form fetches its product when the row opens, so the caller warms it
  // as the pointer arrives.
  onPrefetch,
  onToggleEnabled,
  onDuplicate,
  onRemove,
  onDelete,
  canDelete = true,
  onPlacementSave,
  primaryPrices,
  copy = ORDER_BUMP_COPY,
  ticketTypes,
  // Disabled tickets too, so the rule read out names every ticket it holds.
  allTicketTypes = ticketTypes,
  otherOrderBumps,
  dragHandleProps,
  isSaving,
  // The add panel and an open row are mutually exclusive: two open editors over
  // the same list is a save-order question nobody wants.
  isOpenDisabled = false,
}) {
  const { productId, product, price } = entry;
  const label = product?.title;
  const isDisabled = entry.enabled === false;
  // Hidden is distinct from disabled: a hidden bump is removed from the checkout
  // but revealable via `?show_order_bumps=`.
  const isHidden = entry.hidden === true;
  const isConditional = entry.showHideLogic?.enabled === true;
  const isSoldOut = entry.isSoldOut === true;
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  // Mounted on first open and kept, so a list of rows holds no idle queries
  // and the dialog still animates closed.
  const [hasOpenedInfo, setHasOpenedInfo] = useState(false);
  const openInfo = () => {
    setHasOpenedInfo(true);
    setIsInfoOpen(true);
  };

  const ruleDescription = describeBumpRule(entry.showHideLogic, {
    prices: primaryPrices,
    ticketTypes: allTicketTypes,
    otherOrderBumps,
    copy,
  });

  const infoDialog = hasOpenedInfo && (
    <OrderBumpInfoDialog
      open={isInfoOpen}
      onClose={() => setIsInfoOpen(false)}
      pageId={pageId}
      entry={entry}
      ruleDescription={ruleDescription}
      copy={copy}
    />
  );

  // Still attached, so it must stay removable — but there is nothing to enable,
  // price or configure until whatever broke the offer is fixed on the product.
  if (entry.unavailableReason) {
    return (
      <div className="flex items-center gap-2 border-b px-1 py-3">
        <DragHandle dragHandleProps={dragHandleProps} />
        <div className="opacity-60">
          <Thumbnail media={product?.media} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="truncate text-sm font-semibold text-gray-500 dark:text-muted-foreground">
            {label}
          </span>
          <span className="text-xs text-gray-500 dark:text-muted-foreground">
            {entry.unavailableReason}
          </span>
        </div>
        <ActionsMenu onInfo={openInfo} onRemove={onRemove} copy={copy} />
        {infoDialog}
      </div>
    );
  }

  return (
    <Accordion
      type="single"
      collapsible
      value={isOpen ? productId : ''}
      onValueChange={value => onOpenChange(value === productId)}
      className={cn('transition-colors', (isDisabled || isHidden) && !isOpen && 'opacity-60')}
    >
      <AccordionItem value={productId} className="border-b last:border-b">
        <div className="flex items-center gap-2 px-1">
          <DragHandle dragHandleProps={dragHandleProps} />
          <div className="min-w-0 flex-1">
            <AccordionTrigger
              data-ai-focus={`orderBump:${label}`}
              disabled={isOpenDisabled}
              title={isOpenDisabled ? `Finish adding the new ${copy.noun} first` : undefined}
              onMouseEnter={onPrefetch}
              onFocus={onPrefetch}
              className="min-w-0 items-center gap-2 py-3 hover:no-underline disabled:cursor-not-allowed disabled:opacity-60 [&>span:last-child]:size-7"
            >
              {/* Same anatomy as PriceCard's trigger: what it is called, what
                  kind of price it is, then the headline. */}
              <Thumbnail media={product?.media} />

              <div className="flex min-w-0 flex-1 flex-col leading-tight">
                {/* Badges are for STATE, as in the multi-price list — the
                    subtitle already names the billing type. They lead the
                    column so a row that is not live says so before it says
                    what it is; the row keeps its height when there are none. */}
                {(isDisabled || isSoldOut || isHidden || isConditional) && (
                  <div className="mb-1 flex flex-wrap items-center gap-1">
                    {isDisabled && (
                      <Tooltip label={`Disabled ${copy.plural} will not be shown to customers`}>
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          Disabled
                        </Badge>
                      </Tooltip>
                    )}
                    {isSoldOut && (
                      <Tooltip label="Out of stock on every page that offers it. Customers see it marked sold out and cannot add it.">
                        <StatusBadge
                          status="sold_out"
                          label="Sold out"
                          className="shrink-0 text-xs"
                        />
                      </Tooltip>
                    )}
                    {isHidden && (
                      <Tooltip
                        label={`Hidden ${copy.plural} only show when the URL lists their price reference in ?show_order_bumps=`}
                      >
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          Hidden
                        </Badge>
                      </Tooltip>
                    )}
                    {isConditional && (
                      <Tooltip label={ruleDescription}>
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          Conditional
                        </Badge>
                      </Tooltip>
                    )}
                  </div>
                )}
                <span className="min-w-0 truncate text-sm font-semibold">{label}</span>
                <span className="truncate text-xs font-normal text-gray-500 dark:text-muted-foreground">
                  {getPriceTypeName(price)}
                  {/* A bump product is reusable, so the row says how far a change
                      to it reaches. The count includes this page. */}
                  {entry.usedOnPageCount > 0 && ` · ${bumpUsageLabel(entry.usedOnPageCount)}`}
                </span>
              </div>

              <div className="flex shrink-0 items-center gap-2 pl-2">
                <span className="whitespace-nowrap text-sm font-medium">
                  {/* The resolved offer price is in minor units; the helper
                      defaults to the form's major-unit drafts. */}
                  {getPriceHeadline(price, { amountsInCents: true })}
                </span>
              </div>
            </AccordionTrigger>
          </div>

          <ActionsMenu
            copy={copy}
            onInfo={openInfo}
            isEnabled={!isDisabled}
            onToggleEnabled={onToggleEnabled}
            onDuplicate={onDuplicate}
            onRemove={onRemove}
            onDelete={onDelete}
            canDelete={canDelete}
          />
          {infoDialog}
        </div>

        <AccordionContent>
          <div className="mb-4 border-t border-gray-100 dark:border-border" />
          {/* A disabled placement is inert, the way a disabled variant option is:
              the form greys out and neither Save nor Cancel can be reached. The
              header above stays live, since re-enabling is the way back. */}
          <div className={cn(isDisabled && 'pointer-events-none opacity-50')}>
            <BumpProductForm
              productId={productId}
              primaryPrices={primaryPrices}
              copy={copy}
              ticketTypes={ticketTypes}
              allTicketTypes={allTicketTypes}
              otherOrderBumps={otherOrderBumps}
              preselected={entry.preselected === true}
              hidden={isHidden}
              allowQuantity={entry.allowQuantity === true}
              shortenDescription={entry.shortenDescription === true}
              showHideLogic={entry.showHideLogic}
              onPlacementSave={onPlacementSave}
              onCancel={() => onOpenChange(false)}
              isSaving={isSaving}
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
